import "./easter-eggs.css";

export function triggerShake() {
    document.body.classList.add("shake");

    setTimeout(() => {
        document.body.classList.remove("shake");
    }, 1000);
}

/** Elements of the search results that can be "snapped" away. */
export function getSnapTargets(): HTMLElement[] {
    return Array.from(
        document.querySelectorAll<HTMLElement>(".content-screen .card-post, .content-screen .row-user")
    );
}

const DUST_DURATION = 2200;
const DUST_STAGGER = 900;

/**
 * Disintegrates the given elements into dust (Thanos snap style).
 * Each element gets an SVG filter where a noise mask "eats" the pixels while a
 * displacement map scatters them, and the element drifts up/right.
 *
 * Resolves when the animation ends with a `cleanup` function that restores
 * the elements' inline styles. Call it only after the elements were removed/re-rendered.
 */
export function disintegrate(targets: HTMLElement[]): Promise<() => void> {
    if (!targets.length) return Promise.resolve(() => {});

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", "0");
    svg.setAttribute("height", "0");
    svg.setAttribute("aria-hidden", "true");
    svg.style.position = "absolute";
    svg.style.pointerEvents = "none";

    const stamp = Date.now();
    const base = Math.floor(Math.random() * 1000);

    svg.innerHTML = targets
        .map((_, i) => `
            <filter id="thanos-dust-${stamp}-${i}" x="-40%" y="-40%" width="180%" height="180%" color-interpolation-filters="sRGB">
                <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed="${base + i}" result="rawNoise"/>
                <feComponentTransfer in="rawNoise" result="noise">
                    <feFuncA type="discrete" tableValues="1"/>
                </feComponentTransfer>
                <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  25 0 0 0 -2.5" result="mask"/>
                <feComposite in="SourceGraphic" in2="mask" operator="in" result="eaten"/>
                <feDisplacementMap in="eaten" in2="noise" scale="0" xChannelSelector="R" yChannelSelector="G"/>
            </filter>`)
        .join("");
    document.body.appendChild(svg);

    const filters = Array.from(svg.querySelectorAll("filter"));
    const items = targets.map((el, i) => ({
        el,
        mask: filters[i].querySelector("feColorMatrix") as SVGElement,
        displace: filters[i].querySelector("feDisplacementMap") as SVGElement,
        delay: (i / targets.length) * DUST_STAGGER + Math.random() * 250,
    }));

    items.forEach(({ el }, i) => {
        el.style.filter = `url(#thanos-dust-${stamp}-${i})`;
        el.style.pointerEvents = "none";
        el.style.willChange = "transform, filter, opacity";
    });

    const cleanup = () => {
        items.forEach(({ el }) => {
            el.style.filter = "";
            el.style.transform = "";
            el.style.opacity = "";
            el.style.pointerEvents = "";
            el.style.willChange = "";
        });
        svg.remove();
    };

    return new Promise(resolve => {
        const start = performance.now();

        const frame = (now: number) => {
            let running = false;

            items.forEach(({ el, mask, displace, delay }) => {
                const t = Math.min(Math.max((now - start - delay) / DUST_DURATION, 0), 1);
                if (t < 1) running = true;

                const p = Math.pow(t, 1.5);
                const threshold = 0.1 + 0.8 * p;

                mask.setAttribute("values", `0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  25 0 0 0 ${(-25 * threshold).toFixed(3)}`);
                displace.setAttribute("scale", (p * 140).toFixed(2));

                el.style.transform = `translate(${(p * 60).toFixed(1)}px, ${(-p * 30).toFixed(1)}px) rotate(${(p * 3).toFixed(2)}deg)`;
                el.style.opacity = t > 0.85 ? String(Math.max(0, (1 - t) / 0.15)) : "1";
            });

            if (running) {
                requestAnimationFrame(frame);
            } else {
                resolve(cleanup);
            }
        };

        requestAnimationFrame(frame);
    });
}

/* Google: "do a barrel roll" */
export function triggerBarrelRoll() {
    const root = document.documentElement;
    if (root.classList.contains("barrel-roll")) return;

    root.classList.add("barrel-roll");
    setTimeout(() => root.classList.remove("barrel-roll"), 1100);
}

/* Google: "askew" — toggles a slightly tilted page */
export function toggleAskew() {
    document.documentElement.classList.toggle("askew");
}

/* Google: "gravity" — everything falls to the bottom of the screen, then comes back */
let gravityRunning = false;

export function triggerGravity() {
    if (gravityRunning) return;

    const targets = Array.from(
        document.querySelectorAll<HTMLElement>(
            "header, nav, .card-post, .row-user, .row-fillter-search, .container-search"
        )
    ).filter(el => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
    });
    if (!targets.length) return;

    gravityRunning = true;
    const floor = window.innerHeight;
    document.body.style.overflow = "hidden";

    targets.forEach(el => {
        const r = el.getBoundingClientRect();
        const fall = Math.max(floor - r.bottom, 0) + Math.random() * 12;
        const rotate = (Math.random() - 0.5) * 50;
        const delay = Math.random() * 400;
        const duration = 700 + Math.random() * 500;

        el.style.transition = `transform ${duration}ms cubic-bezier(0.55, 0, 1, 0.45) ${delay}ms`;
        el.style.pointerEvents = "none";
        // next frame so the transition kicks in
        requestAnimationFrame(() => {
            el.style.transform = `translateY(${fall}px) rotate(${rotate}deg)`;
        });
    });

    // let it rest on the floor, then bring everything back
    setTimeout(() => {
        targets.forEach(el => {
            el.style.transition = "transform 700ms ease-in-out";
            el.style.transform = "";
        });
    }, 3500);

    setTimeout(() => {
        targets.forEach(el => {
            el.style.transition = "";
            el.style.pointerEvents = "";
        });
        document.body.style.overflow = "";
        gravityRunning = false;
    }, 4300);
}
